/**
 * Reference data service client — talks to the standalone apmt-cdh-reference-data-service.
 *
 * The backend contract is keyed by (terminal, app, tableKey) and uses:
 *   - schemas with UUID `schemaId`, a single `version`, and a `columns` array
 *   - rows with UUID `rowId`, a `data` object, and a single `version`
 *   - cursor pagination ({ items, nextCursor, hasMore })
 *   - RFC 7807 errors
 *
 * To keep the existing UI components (grid, column builder, preview) stable, this module acts as
 * an ADAPTER: it maps the backend shapes to/from the field names the components already use
 * (`columnDefs` with `label`/`type`/`refTable`, `RefRow.id`, etc.). New-contract specifics that
 * have no UI equivalent (e.g. ref `terminal`/`app`) are derived from the current tenancy.
 *
 * Base path: REF_DATA_API_BASE (e.g. http://localhost:8087/api/v1)
 */
import { ApiClient } from './apiClient';
import { REF_DATA_API_BASE } from '../config/api';

const client = new ApiClient(REF_DATA_API_BASE);

// ─── UI-facing types (stable for components) ───────────────────────────────────

export interface RefSchema {
  /** Backend UUID schema id. */
  schemaId?: string;
  applicationId: string;
  tableKey: string;
  displayName: string | null;
  description: string | null;
  /** Column descriptors in the UI shape ({ key, label, type, required, options, refTable }). */
  columnDefs: Array<Record<string, unknown>>;
  /** Retained for component compatibility; the backend has no uiHints. */
  uiHints: Record<string, unknown>;
  /** Mapped from the backend's single `version`. */
  schemaVersion: number;
  majorVersion: number;
  active?: boolean;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface RefSchemaHistoryEntry {
  schemaVersion: number;
  majorVersion: number;
  columnDefs: Array<Record<string, unknown>>;
  changeSummary: string | null;
  changedBy: string;
  changedAt: string;
  changeType?: string;
  payload?: unknown;
}

export interface RefRow {
  /** Backend UUID row id (string). */
  id: string;
  /** Row data as a plain object. */
  data: Record<string, unknown>;
  /** Not modelled by the backend; kept optional for component compatibility. */
  sortOrder?: number;
  active?: boolean;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

/** One cursor page of rows, mirroring the backend's { items, nextCursor, hasMore } contract. */
export interface RefRowPage {
  items: RefRow[];
  /** Opaque cursor for the next page, or null when there are no more rows. */
  nextCursor: string | null;
  hasMore: boolean;
}

export interface CreateSchemaRequest {
  tableKey: string;
  displayName?: string;
  description?: string;
  columnDefs?: Array<Record<string, unknown>>;
  uiHints?: Record<string, unknown>;
}

export interface UpdateSchemaRequest {
  displayName?: string;
  description?: string;
  columnDefs?: Array<Record<string, unknown>>;
  changeSummary?: string;
}

export interface CreateRowRequest {
  data: Record<string, unknown>;
  sortOrder?: number;
}

export interface UpdateRowRequest {
  data?: Record<string, unknown>;
  sortOrder?: number;
}

export interface BulkReplaceRequest {
  rows: Array<Record<string, unknown>>;
}

// ─── Backend DTOs (as returned by reference-data-service) ───────────────────────

interface BackendColumn {
  key: string;
  displayName?: string;
  type: string; // STRING | NUMBER | BOOLEAN | DATE | DATETIME | ENUM | REF
  required?: boolean;
  immutable?: boolean;
  enumOptions?: string[];
  ref?: { terminal: string; app: string; tableKey: string } | null;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  pattern?: string;
}

interface BackendSchema {
  schemaId: string;
  terminalId: string;
  appId: string;
  tableKey: string;
  displayName: string | null;
  description: string | null;
  columns: BackendColumn[];
  version: number;
  containsPii: boolean;
  active: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

interface BackendRow {
  rowId: string;
  schemaId: string;
  terminalId: string;
  appId: string;
  tableKey: string;
  data: Record<string, unknown>;
  version: number;
  active: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

interface BackendPagedRows {
  items: BackendRow[];
  nextCursor: string | null;
  hasMore: boolean;
}

interface BackendSnapshot {
  snapshotId: string;
  version: number;
  changeType: string;
  payload: unknown;
  changeSummary: string | null;
  createdAt: string;
  createdBy: string;
}

// ─── Column type mapping (UI ↔ backend) ─────────────────────────────────────────

const UI_TO_BACKEND_TYPE: Record<string, string> = {
  text: 'STRING',
  string: 'STRING',
  number: 'NUMBER',
  boolean: 'BOOLEAN',
  date: 'DATE',
  datetime: 'DATETIME',
  select: 'ENUM',
  enum: 'ENUM',
  ref: 'REF',
};

const BACKEND_TO_UI_TYPE: Record<string, string> = {
  STRING: 'text',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  DATE: 'date',
  DATETIME: 'text',
  ENUM: 'select',
  REF: 'ref',
};

function backendColToUi(c: BackendColumn): Record<string, unknown> {
  // DATETIME columns are always auto-captured timestamps — treat them as
  // immutable in the UI regardless of what the backend schema says, so they
  // are never shown as editable fields in the row edit dialog.
  const isDatetime = c.type === 'DATETIME';
  const ui: Record<string, unknown> = {
    key: c.key,
    label: c.displayName ?? c.key,
    type: BACKEND_TO_UI_TYPE[c.type] ?? 'text',
    required: !!c.required,
    immutable: !!c.immutable || isDatetime,
    // Preserve the raw backend type so InlineDataGrid can render datetimes nicely
    backendType: c.type,
  };
  if (c.enumOptions) ui.options = c.enumOptions;
  if (c.ref) ui.refTable = c.ref.tableKey;
  if (c.maxLength != null) ui.maxLength = c.maxLength;
  if (c.pattern != null) ui.pattern = c.pattern;
  return ui;
}

function uiColToBackend(c: Record<string, any>, terminal: string, app: string): BackendColumn {
  const type = UI_TO_BACKEND_TYPE[String(c.type ?? 'text').toLowerCase()] ?? 'STRING';
  const out: BackendColumn = {
    key: c.key,
    displayName: c.label ?? c.displayName ?? c.key,
    type,
    required: !!c.required,
  };
  if (type === 'ENUM') out.enumOptions = c.options ?? c.enumOptions ?? [];
  if (type === 'REF' && (c.refTable || c.ref?.tableKey)) {
    out.ref = { terminal, app, tableKey: c.refTable ?? c.ref.tableKey };
  }
  if (c.maxLength != null) out.maxLength = c.maxLength;
  if (c.pattern != null) out.pattern = c.pattern;
  return out;
}

function mapSchema(b: BackendSchema): RefSchema {
  return {
    schemaId: b.schemaId,
    applicationId: b.appId,
    tableKey: b.tableKey,
    displayName: b.displayName,
    description: b.description,
    columnDefs: (b.columns ?? []).map(backendColToUi),
    uiHints: {},
    schemaVersion: b.version,
    majorVersion: 0,
    active: b.active,
    isActive: b.active,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
    createdBy: b.createdBy,
    updatedBy: b.updatedBy,
  };
}

function mapRow(b: BackendRow): RefRow {
  return {
    id: b.rowId,
    data: b.data ?? {},
    active: b.active,
    isActive: b.active,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
    createdBy: b.createdBy,
    updatedBy: b.updatedBy,
  };
}

function mapSnapshot(b: BackendSnapshot): RefSchemaHistoryEntry {
  const cols = Array.isArray(b.payload) ? (b.payload as BackendColumn[]).map(backendColToUi) : [];
  return {
    schemaVersion: b.version,
    majorVersion: 0,
    columnDefs: cols,
    changeSummary: b.changeSummary,
    changedBy: b.createdBy,
    changedAt: b.createdAt,
    changeType: b.changeType,
    payload: b.payload,
  };
}

// ─── API ─────────────────────────────────────────────────────────────────────

/** Max rows fetched per table view. The backend caps page size at 500. */
const ROW_PAGE_LIMIT = 500;

export const refDataService = {

  // ─ Schemas ─────────────────────────────────────────────────────────────────

  listSchemas: async (terminal: string, app: string): Promise<RefSchema[]> => {
    const res = await client.get<BackendSchema[]>(`/${terminal}/${app}/schemas`);
    return res.map(mapSchema);
  },

  getSchema: async (terminal: string, app: string, tableKey: string): Promise<RefSchema> => {
    const res = await client.get<BackendSchema>(`/${terminal}/${app}/schemas/${tableKey}`);
    return mapSchema(res);
  },

  createSchema: async (terminal: string, app: string, body: CreateSchemaRequest): Promise<RefSchema> => {
    const res = await client.post<BackendSchema>(`/${terminal}/${app}/schemas`, {
      tableKey: body.tableKey,
      displayName: body.displayName ?? body.tableKey,
      description: body.description,
      columns: (body.columnDefs ?? []).map(c => uiColToBackend(c, terminal, app)),
      containsPii: false,
    });
    return mapSchema(res);
  },

  uploadCsv: async (
    terminal: string,
    app: string,
    file: File,
    tableKey?: string,
    displayName?: string,
    description?: string,
    containsPii: boolean = false,
  ): Promise<RefSchema> => {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    if (tableKey) params.append('tableKey', tableKey);
    if (displayName) params.append('displayName', displayName);
    if (description) params.append('description', description);
    params.append('containsPii', String(containsPii));

    const res = await client.post<BackendSchema>(
      `/${terminal}/${app}/schemas/upload-csv?${params.toString()}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return mapSchema(res);
  },

  updateSchema: async (
    terminal: string,
    app: string,
    tableKey: string,
    body: UpdateSchemaRequest,
  ): Promise<RefSchema> => {
    const res = await client.patch<BackendSchema>(`/${terminal}/${app}/schemas/${tableKey}`, {
      displayName: body.displayName ?? tableKey,
      description: body.description,
      columns: (body.columnDefs ?? []).map(c => uiColToBackend(c, terminal, app)),
      containsPii: false,
    });
    return mapSchema(res);
  },

  getSchemaHistory: async (terminal: string, app: string, tableKey: string): Promise<RefSchemaHistoryEntry[]> => {
    const res = await client.get<BackendSnapshot[]>(`/${terminal}/${app}/schemas/${tableKey}/history`);
    return res.map(mapSnapshot);
  },

  // ─ Rows ────────────────────────────────────────────────────────────────────

  createRow: async (terminal: string, app: string, tableKey: string, body: CreateRowRequest): Promise<RefRow> => {
    const res = await client.post<BackendRow>(`/${terminal}/${app}/schemas/${tableKey}/rows`, {
      data: body.data,
    });
    return mapRow(res);
  },

  updateRow: async (
    terminal: string,
    app: string,
    tableKey: string,
    rowId: string,
    body: UpdateRowRequest,
  ): Promise<RefRow> => {
    const res = await client.put<BackendRow>(`/${terminal}/${app}/schemas/${tableKey}/rows/${rowId}`, {
      data: body.data,
    });
    return mapRow(res);
  },

  deleteRow: (terminal: string, app: string, tableKey: string, rowId: string): Promise<void> =>
    client.delete<void>(`/${terminal}/${app}/schemas/${tableKey}/rows/${rowId}`),

  // ─ Rows ────────────────────────────────────────────────────────────────────

  /**
   * Fetch a single cursor page of rows. Supports an optional case-insensitive substring filter `q`
   * (matched server-side across the whole row payload). Returns the page envelope so callers can
   * drive infinite scroll from `nextCursor`/`hasMore`.
   */
  getRowsPage: async (
    terminal: string,
    app: string,
    tableKey: string,
    opts: { cursor?: string | number; limit?: number; q?: string } = {},
  ): Promise<RefRowPage> => {
    const capped = Math.min(opts.limit ?? ROW_PAGE_LIMIT, ROW_PAGE_LIMIT);
    const cursor = opts.cursor ?? 0;
    const params = new URLSearchParams({ cursor: String(cursor), limit: String(capped) });
    const term = (opts.q ?? '').trim();
    if (term) params.set('q', term);
    const res = await client.get<BackendPagedRows>(
      `/${terminal}/${app}/schemas/${tableKey}/rows?${params.toString()}`,
    );
    return {
      items: (res.items ?? []).map(mapRow),
      nextCursor: res.nextCursor ?? null,
      hasMore: !!res.hasMore,
    };
  },

  getRows: async (
    terminal: string,
    app: string,
    tableKey: string,
    cursor: string | number = 0,
    limit = ROW_PAGE_LIMIT,
  ): Promise<RefRow[]> => {
    const capped = Math.min(limit, ROW_PAGE_LIMIT);
    const res = await client.get<BackendPagedRows>(
      `/${terminal}/${app}/schemas/${tableKey}/rows?cursor=${cursor}&limit=${capped}`,
    );
    return (res.items ?? []).map(mapRow);
  },

  /**
   * Fetch one page of a row's audit history, newest first. Pass `beforeVersion` (the oldest version
   * already loaded) to page backwards through older snapshots; omit it for the first page.
   */
  getRowHistory: async (
    terminal: string,
    app: string,
    tableKey: string,
    rowId: string,
    opts: { beforeVersion?: number; limit?: number } = {},
  ): Promise<RefSchemaHistoryEntry[]> => {
    const params = new URLSearchParams({ limit: String(opts.limit ?? 20) });
    if (opts.beforeVersion != null) params.set('beforeVersion', String(opts.beforeVersion));
    const res = await client.get<BackendSnapshot[]>(
      `/${terminal}/${app}/schemas/${tableKey}/rows/${rowId}/history?${params.toString()}`,
    );
    return res.map(mapSnapshot);
  },

  bulkReplace: (
    terminal: string,
    app: string,
    tableKey: string,
    body: BulkReplaceRequest,
  ): Promise<RefRow[]> =>
    client.post<RefRow[]>(`/${terminal}/${app}/schemas/${tableKey}/rows/bulk`, {
      rows: body.rows.map(data => ({ data })),
    }),
};

// ─── Parsing helpers (kept for component compatibility) ─────────────────────────

/** The backend has no uiHints; return safe defaults. */
export function parseUiHints(_schema: RefSchema): {
  layout: string;
  gridCols: number;
  gridRows: number;
  [key: string]: unknown;
} {
  return { layout: 'table', gridCols: 12, gridRows: 10 };
}

/** Column definitions arrive pre-mapped into the UI shape. */
export function parseColumnDefs(schema: RefSchema): Array<{
  key: string;
  label: string;
  type?: string;
  required?: boolean;
  [key: string]: unknown;
}> {
  return Array.isArray(schema.columnDefs) ? (schema.columnDefs as any[]) : [];
}

/** Row data arrives pre-parsed as an object. */
export function parseRowData(row: RefRow): Record<string, unknown> {
  return row.data && typeof row.data === 'object' ? row.data : {};
}
